import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  LucideAngularModule,
  ArrowLeft,
  ChevronDown,
  CheckCircle2,
  AlertTriangle,
  Target,
  Lightbulb,
  MessageSquare,
  User,
  FileText,
  Loader2,
} from 'lucide-angular';
import {
  GradingResultDetail,
  GradingRunStudentResult,
  ComputedScore,
} from '../../../grading.types';

/**
 * Display data for a question/item with computed score from backend
 */
interface QuestionDisplayData {
  itemId: string;
  sectionId: string;
  questionNumber: number;
  questionText: string;
  feedback: string;
  /** Computed score from backend */
  score: ComputedScore;
  criteriaCount: number;
}

@Component({
  selector: 'app-student-result-details',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './student-result-details.component.html',
  styleUrl: './student-result-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StudentResultDetailsComponent {
  /** Student summary data */
  readonly student = input.required<GradingRunStudentResult>();

  /** Detailed grading result (may be loading) */
  readonly resultDetail = input<GradingResultDetail | null>(null);

  /** Loading state */
  readonly isLoading = input<boolean>(false);

  /** Error message if loading failed */
  readonly error = input<string | null>(null);

  /** Emits when back button is clicked */
  readonly back = output<void>();

  protected readonly icons = {
    ArrowLeft,
    ChevronDown,
    CheckCircle2,
    AlertTriangle,
    Target,
    Lightbulb,
    MessageSquare,
    User,
    FileText,
    Loader2,
  };

  /** Track expanded questions */
  protected readonly expandedQuestions = signal<Set<string>>(new Set());

  /** Build a map of itemId -> questionText from questionAnalysis */
  private readonly questionTextMap = computed<Map<string, string>>(() => {
    const detail = this.resultDetail();
    const map = new Map<string, string>();

    if (detail?.questionAnalysis) {
      for (const section of detail.questionAnalysis.sections) {
        for (const item of section.items) {
          map.set(item.itemId, item.questionText);
        }
      }
    }

    return map;
  });

  /** Computed questions display data - uses score from backend */
  protected readonly questionsData = computed<QuestionDisplayData[]>(() => {
    const detail = this.resultDetail();
    if (!detail) return [];

    const textMap = this.questionTextMap();
    const questions: QuestionDisplayData[] = [];
    let questionNumber = 1;

    for (const section of detail.sectionEvaluations) {
      for (const item of section.itemEvaluations) {
        questions.push({
          itemId: item.itemId,
          sectionId: section.sectionId,
          questionNumber: questionNumber++,
          questionText: textMap.get(item.itemId) || `Question ${questionNumber - 1}`,
          feedback: item.feedback,
          score: item.score, // Use computed score directly from backend
          criteriaCount: item.criteriaEvaluations.length,
        });
      }
    }

    return questions;
  });

  /** Get score color class based on percentage */
  protected getScoreColorClass(percentage: number): string {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    if (percentage >= 40) return 'text-orange-600';
    return 'text-red-500';
  }

  /** Get score background class based on percentage */
  protected getScoreBgClass(percentage: number): string {
    if (percentage >= 80) return 'bg-green-100 text-green-700';
    if (percentage >= 60) return 'bg-yellow-100 text-yellow-700';
    if (percentage >= 40) return 'bg-orange-100 text-orange-700';
    return 'bg-red-100 text-red-600';
  }

  /** Toggle question expansion */
  protected toggleQuestion(itemId: string): void {
    const current = this.expandedQuestions();
    const newSet = new Set(current);
    if (newSet.has(itemId)) {
      newSet.delete(itemId);
    } else {
      newSet.add(itemId);
    }
    this.expandedQuestions.set(newSet);
  }

  /** Check if question is expanded */
  protected isQuestionExpanded(itemId: string): boolean {
    return this.expandedQuestions().has(itemId);
  }

  /** Handle back button click */
  protected onBack(): void {
    this.back.emit();
  }

  /** Get overall score from result detail */
  protected readonly overallScore = computed(() => {
    const detail = this.resultDetail();
    return detail?.score ?? null;
  });

  /** Get overall percentage from student's computed score */
  protected readonly overallPercentage = computed(() => {
    const student = this.student();
    return student.score?.percentage ?? 0;
  });

  /** Format score display - shows earned/max points */
  protected formatScore(): string {
    const student = this.student();
    if (student.score) {
      return `${student.score.earnedPoints}/${student.score.maxPoints}`;
    }
    return '-';
  }
}
